/**
 * PortfolioGreeks — net option EXPOSURE (audit C7) across ALL open positions: manual/campaign
 * baskets AND auto-engine cycles, from GET /options/portfolio-greeks (same BS engine + lot/fx
 * convention as the builder, so numbers agree).
 *
 * Cross-underlying honesty: raw Δ (BTC units vs NIFTY shares) is NOT summable, so the headline
 * uses ₹-notional directional exposure (Δ × spot × fx); Θ (₹/day) and Vega (₹ per 1% IV) already
 * are ₹. Valued at each position's ENTRY spot — a directional/vol POSTURE glance.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Tooltip, CircularProgress } from '@mui/material';
import { optionsService } from '../../services/options.service';
import { useOptionColors, inrSigned } from './shared/OptionsUi';

interface Und { symbol: string; delta: number; deltaInr: number; theta: number; vega: number; positions: number }
interface Greeks { asOf: string; positions: number; net: { deltaInr: number; theta: number; vega: number }; byUnderlying: Und[] }

const PortfolioGreeks: React.FC = () => {
  const c = useOptionColors();
  const [g, setG] = useState<Greeks | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await optionsService.getPortfolioGreeks(); setG(r?.data?.data || null); }
    catch { /* keep last */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30_000); return () => clearInterval(id); }, [load]);

  if (!g || !g.positions) return null; // nothing open → hide entirely
  const { net } = g;

  const Metric: React.FC<{ label: string; value: string; color?: string; tip: React.ReactNode }> = ({ label, value, color, tip }) => (
    <Tooltip arrow title={tip}>
      <Box sx={{ minWidth: 120, cursor: 'help' }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px dotted', borderColor: 'text.disabled', display: 'inline-block' }}>{label}</Typography>
        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
      </Box>
    </Tooltip>
  );

  const dir = net.deltaInr;
  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800 }}>Net option exposure</Typography>
          <Chip size="small" label="at entry" sx={{ height: 18, fontSize: '0.56rem', fontWeight: 700 }} />
          <Typography variant="caption" color="text.secondary">across {g.positions} open position{g.positions === 1 ? '' : 's'}</Typography>
          {loading && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: g.byUnderlying.length > 1 ? 1.25 : 0 }}>
          <Metric label="Directional" value={`${dir >= 0 ? 'Long' : 'Short'} ${inrSigned(Math.abs(dir))}`} color={dir >= 0 ? c.profit : c.loss}
            tip={<>₹-notional directional exposure (Δ × spot). {dir >= 0 ? 'Net LONG — the book gains when the underlying rises.' : 'Net SHORT — the book gains when the underlying falls.'} ≈ like holding {inrSigned(Math.abs(dir))} of the underlying{dir >= 0 ? '' : ' short'}.</>} />
          <Metric label="Theta" value={`${inrSigned(net.theta)}/day`} color={net.theta >= 0 ? c.profit : c.loss}
            tip={net.theta >= 0 ? <>Time decay works FOR you — the book earns ≈ {inrSigned(net.theta)} for every day price sits still.</> : <>Time decay works AGAINST you — the book loses ≈ {inrSigned(Math.abs(net.theta))} per day; your move needs to arrive soon.</>} />
          <Metric label="Vega" value={`${inrSigned(net.vega)} / 1% IV`} color={net.vega >= 0 ? c.profit : c.loss}
            tip={net.vega >= 0 ? <>Long volatility — a 1% RISE in implied volatility adds ≈ {inrSigned(net.vega)}.</> : <>Short volatility — a 1% FALL in implied volatility adds ≈ {inrSigned(Math.abs(net.vega))} (a rise costs you).</>} />
        </Box>

        {g.byUnderlying.length > 1 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
            {g.byUnderlying.map(u => (
              <Tooltip key={u.symbol} arrow title={<>{u.symbol}: {u.delta >= 0 ? 'net long' : 'net short'} (Δ {u.delta}) · θ {inrSigned(u.theta)}/day · vega {inrSigned(u.vega)}/1%</>}>
                <Chip size="small" label={`${u.symbol} ${u.deltaInr >= 0 ? '↑' : '↓'} ${inrSigned(u.deltaInr)}`}
                  sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: u.deltaInr >= 0 ? c.profitBg : c.lossBg, color: u.deltaInr >= 0 ? c.profit : c.loss }} />
              </Tooltip>
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Valued at each position's entry price — a directional / volatility posture, not a tick-by-tick figure.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PortfolioGreeks;
